import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

function SortCategoriesModal({ categories, onReorder, isOpen, onClose }) {
  const [orderedCategories, setOrderedCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    if (isOpen && categories) {
      // Build hierarchical structure
      const parentCats = categories.filter(c => !c.parent_id).sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
      const childCats = categories.filter(c => c.parent_id);
      
      const ordered = [];
      parentCats.forEach(parent => {
        ordered.push({ ...parent, level: 0 });
        const children = childCats.filter(c => c.parent_id === parent.id).sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
        children.forEach(child => {
          ordered.push({ ...child, level: 1 });
        });
      });
      
      setOrderedCategories(ordered);
    }
  }, [isOpen, categories]);

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newOrder = [...orderedCategories];
    const draggedItem = newOrder[draggedIndex];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(index, 0, draggedItem);
    
    setOrderedCategories(newOrder);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const categoryOrders = orderedCategories.map((category, index) => ({
        id: category.id,
        order_index: index
      }));
      console.log('Saving category order:', categoryOrders);
      await onReorder(categoryOrders);
    } catch (error) {
      console.error('Failed to save order:', error);
    } finally {
      setSaving(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Sort Categories</DialogTitle>
          <DialogDescription>
            Drag and drop categories to reorder them.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 overflow-y-auto py-4 flex-1">
          {orderedCategories.map((category, index) => (
            <div
              key={category.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`flex items-center gap-3 p-3 border rounded cursor-move transition-all ${
                draggedIndex === index ? 'opacity-50 bg-blue-50' : 'hover:bg-gray-50'
              }`}
            >
              <div className="text-gray-400" style={{ marginLeft: `${(category.level || 0) * 24}px` }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="font-medium">
                  {category.level === 1 && '↳ '}
                  {category.name}
                </div>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving ? 'Saving...' : 'Save Order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SortCategoriesModal;
